import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BadgeDollarSign,
  Check,
  CircleDollarSign,
  Copy,
  Edit3,
  Gauge,
  Leaf,
  MoreHorizontal,
  Plus,
  Settings2,
  ShieldCheck,
  Sparkles,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import CostProfileForm from './CostProfileForm';
import ConfirmDialog from './ConfirmDialog';
import { db } from '../lib/firebase';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const shortNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(value) || 0;
}

function sortedProfiles(items) {
  return [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    const updatedDifference = timestampValue(b.updatedAt) - timestampValue(a.updatedAt);
    if (updatedDifference) return updatedDifference;
    return a.name.localeCompare(b.name);
  });
}

function costPerGram(profile) {
  return profile.spoolWeightGrams > 0 ? profile.spoolPrice / profile.spoolWeightGrams : 0;
}

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked this change. Confirm the user subcollection rules are published.';
  if (code.includes('unavailable')) return 'MakerMargin could not reach Firebase. Check your connection and try again.';
  return error?.message || 'The cost profile could not be updated.';
}

export default function CostProfiles({ user, onRequestAuth }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  const showToast = (message) => {
    window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => setToast(''), 3200);
  };

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    setProfiles([]);
    setError('');
    if (!user || !db) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const reference = collection(db, 'users', user.uid, 'costProfiles');
    return onSnapshot(reference, (snapshot) => {
      const items = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      setProfiles(sortedProfiles(items));
      setLoading(false);
    }, (snapshotError) => {
      setError(readableError(snapshotError));
      setLoading(false);
    });
  }, [user]);

  const defaultProfile = profiles.find((profile) => profile.isDefault) || profiles[0];
  const averageMargin = useMemo(() => {
    if (!profiles.length) return 0;
    return profiles.reduce((sum, profile) => sum + Number(profile.targetMargin || 0), 0) / profiles.length;
  }, [profiles]);
  const materials = useMemo(() => new Set(profiles.map((profile) => profile.materialType)).size, [profiles]);

  const saveProfile = async (values) => {
    if (!user || !db) throw new Error('Sign in before saving a cost profile.');
    setWorking(true);
    try {
      const isEditing = Boolean(editor?.id);
      const profilesCollection = collection(db, 'users', user.uid, 'costProfiles');
      const target = isEditing ? doc(profilesCollection, editor.id) : doc(profilesCollection);
      const shouldDefault = values.isDefault || profiles.length === 0;
      const batch = writeBatch(db);

      if (shouldDefault) {
        profiles.forEach((profile) => {
          if (profile.id !== target.id && profile.isDefault) {
            batch.set(doc(profilesCollection, profile.id), { isDefault: false, updatedAt: serverTimestamp() }, { merge: true });
          }
        });
      }

      batch.set(target, {
        ...values,
        ownerId: user.uid,
        isDefault: shouldDefault || Boolean(editor?.isDefault),
        ...(isEditing ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await batch.commit();
      setEditor(null);
      showToast(isEditing ? 'Cost profile updated.' : 'Cost profile created.');
    } catch (saveError) {
      throw new Error(readableError(saveError));
    } finally {
      setWorking(false);
    }
  };

  const setDefault = async (profile) => {
    if (!user || !db || profile.isDefault) return;
    setOpenMenu(null);
    setWorking(true);
    try {
      const profilesCollection = collection(db, 'users', user.uid, 'costProfiles');
      const batch = writeBatch(db);
      profiles.forEach((item) => {
        batch.set(doc(profilesCollection, item.id), {
          isDefault: item.id === profile.id,
          ...(item.id === profile.id ? { updatedAt: serverTimestamp() } : {}),
        }, { merge: true });
      });
      await batch.commit();
      showToast(`${profile.name} is now your default.`);
    } catch (defaultError) {
      setError(readableError(defaultError));
    } finally {
      setWorking(false);
    }
  };

  const duplicateProfile = async (profile) => {
    if (!user || !db) return;
    setOpenMenu(null);
    setWorking(true);
    try {
      const reference = doc(collection(db, 'users', user.uid, 'costProfiles'));
      const copy = { ...profile };
      delete copy.id;
      delete copy.createdAt;
      delete copy.updatedAt;
      await setDoc(reference, {
        ...copy,
        name: `${profile.name} Copy`,
        isDefault: false,
        ownerId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      showToast('Profile duplicated.');
    } catch (duplicateError) {
      setError(readableError(duplicateError));
    } finally {
      setWorking(false);
    }
  };

  const deleteProfile = async () => {
    if (!deleteTarget || !user || !db) return;
    setWorking(true);
    try {
      const profilesCollection = collection(db, 'users', user.uid, 'costProfiles');
      const remaining = profiles.filter((profile) => profile.id !== deleteTarget.id);
      const batch = writeBatch(db);
      batch.delete(doc(profilesCollection, deleteTarget.id));
      if (deleteTarget.isDefault && remaining.length) {
        batch.set(doc(profilesCollection, remaining[0].id), { isDefault: true, updatedAt: serverTimestamp() }, { merge: true });
      }
      await batch.commit();
      showToast('Cost profile deleted.');
      setDeleteTarget(null);
    } catch (deleteError) {
      setError(readableError(deleteError));
    } finally {
      setWorking(false);
    }
  };

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><ShieldCheck size={30} /></span>
        <p className="eyebrow">Private workspace</p>
        <h1>Sign in to build your cost profiles.</h1>
        <p>Your material prices, electricity rates, labor assumptions, and margins stay attached to your MakerMargin account.</p>
        <div className="signed-out-state__actions">
          <button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button>
          <button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button>
        </div>
      </section>
    );
  }

  return (
    <section className="profiles-page">
      <header className="profiles-page__hero">
        <div>
          <p className="eyebrow">Phase 2 · Cost intelligence</p>
          <h1>Price from your real costs.</h1>
          <p>Create reusable profiles for each material, printer workflow, or production setup. Every future estimate can start from a trusted baseline.</p>
        </div>
        <button className="button button--primary button--large" type="button" onClick={() => setEditor({})}><Plus size={19} /> New cost profile</button>
      </header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}

      <section className="profiles-summary" aria-label="Cost profile summary">
        <article><span><Settings2 size={19} /></span><div><small>Saved profiles</small><strong>{profiles.length}</strong></div></article>
        <article><span><Star size={19} /></span><div><small>Current default</small><strong>{defaultProfile?.name || 'Not set'}</strong></div></article>
        <article><span><Gauge size={19} /></span><div><small>Average target margin</small><strong>{shortNumber.format(averageMargin)}%</strong></div></article>
        <article><span><Leaf size={19} /></span><div><small>Materials covered</small><strong>{materials}</strong></div></article>
      </section>

      <div className="profiles-layout">
        <section className="profiles-library brushed-panel">
          <div className="section-heading profiles-library__heading">
            <div><p className="eyebrow">Your pricing foundations</p><h2>Cost profile library</h2></div>
            <small>{profiles.length ? `${profiles.length} saved` : 'Ready for your first profile'}</small>
          </div>

          {loading ? (
            <div className="profiles-loading" aria-live="polite"><span /><span /><span /><p>Loading your cost profiles…</p></div>
          ) : profiles.length === 0 ? (
            <div className="profiles-empty">
              <span><Sparkles size={28} /></span>
              <h3>Create your first pricing baseline.</h3>
              <p>Start with the filament you use most. MakerMargin will automatically make the first profile your default.</p>
              <button className="button button--primary" type="button" onClick={() => setEditor({})}><Plus size={18} /> Create first profile</button>
            </div>
          ) : (
            <div className="profile-card-grid">
              {profiles.map((profile) => {
                return (
                  <article className={`profile-card ${profile.isDefault ? 'profile-card--default' : ''}`} key={profile.id}>
                    <div className="profile-card__top">
                      <span className="profile-card__material">{profile.materialType || 'Other'}</span>
                      {profile.isDefault && <span className="profile-card__default"><Star size={13} /> Default</span>}
                      <div className="profile-card__menu-wrap">
                        <button className="icon-button profile-card__menu" type="button" onClick={() => setOpenMenu(openMenu === profile.id ? null : profile.id)} aria-label={`Actions for ${profile.name}`} aria-expanded={openMenu === profile.id}><MoreHorizontal size={18} /></button>
                        {openMenu === profile.id && (
                          <div className="profile-menu">
                            <button type="button" onClick={() => { setEditor(profile); setOpenMenu(null); }}><Edit3 size={16} /> Edit profile</button>
                            <button type="button" onClick={() => duplicateProfile(profile)}><Copy size={16} /> Duplicate</button>
                            {!profile.isDefault && <button type="button" onClick={() => setDefault(profile)}><Star size={16} /> Make default</button>}
                            <button className="profile-menu__danger" type="button" onClick={() => { setDeleteTarget(profile); setOpenMenu(null); }}><Trash2 size={16} /> Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <h3>{profile.name}</h3>
                    <p>{profile.brand || `${profile.materialType || 'Material'} profile`}</p>
                    <div className="profile-card__economics">
                      <div><small>Material</small><strong>{currency.format(costPerGram(profile))}<span>/g</span></strong></div>
                      <div><small>Electricity</small><strong>{currency.format(Number(profile.electricityRate || 0))}<span>/kWh</span></strong></div>
                      <div><small>Machine</small><strong>{currency.format(Number(profile.machineRate || 0))}<span>/hr</span></strong></div>
                      <div><small>Labor</small><strong>{currency.format(Number(profile.laborRate || 0))}<span>/hr</span></strong></div>
                    </div>
                    <div className="profile-card__footer">
                      <div><small>Target margin</small><strong>{shortNumber.format(Number(profile.targetMargin || 0))}%</strong></div>
                      <div><small>Failure allowance</small><strong>{shortNumber.format(Number(profile.wastePercent || 0))}%</strong></div>
                      <button type="button" onClick={() => setEditor(profile)}>Edit <Edit3 size={15} /></button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <aside className="profiles-insight metal-panel">
          <div className="profiles-insight__mark"><BadgeDollarSign size={23} /></div>
          <p className="eyebrow">Why profiles matter</p>
          <h2>One reliable source of truth.</h2>
          <p>When filament prices or utility rates change, update the profile once. Every reopened calculation can use the new assumptions without rebuilding the job.</p>
          <div className="profiles-insight__steps">
            <span><Check size={15} /><div><strong>Reusable defaults</strong><small>Start estimates with trusted values.</small></div></span>
            <span><Zap size={15} /><div><strong>Instant recalculation</strong><small>Changes flow into future pricing.</small></div></span>
            <span><CircleDollarSign size={15} /><div><strong>Margin protection</strong><small>Know what each operating choice costs.</small></div></span>
          </div>
          {defaultProfile && (
            <div className="profiles-insight__default">
              <span><Star size={15} /> Active default</span>
              <strong>{defaultProfile.name}</strong>
              <small>{defaultProfile.materialType} · {shortNumber.format(defaultProfile.targetMargin)}% target margin</small>
            </div>
          )}
        </aside>
      </div>

      {editor && <CostProfileForm profile={editor.id ? editor : null} isFirstProfile={profiles.length === 0} onClose={() => !working && setEditor(null)} onSave={saveProfile} saving={working} />}
      {deleteTarget && <ConfirmDialog title={`Delete ${deleteTarget.name}?`} message={deleteTarget.isDefault && profiles.length > 1 ? 'This is your current default. MakerMargin will promote another saved profile after deletion.' : 'This removes the saved pricing assumptions. Existing calculations are not deleted.'} working={working} onCancel={() => !working && setDeleteTarget(null)} onConfirm={deleteProfile} />}
      {toast && <div className="toast" role="status"><Check size={17} /> {toast}</div>}
    </section>
  );
}
